import { useRef, useState } from 'react'
import classNames from 'classnames'

import { useScreen } from '../../../hooks'
import { FloatingSelectDropdown } from '../../Floating'
import { InputField } from '../../Input'
import Modal, { Box } from '../../Modal'
import { COLOR_FORMAT_HEX } from '../ColorPicker.constants'
import { ColorPickerPopover } from '../ColorPickerPopover'

import {
  FIELD_ACTIVE_VALUE_KEY,
  FIELD_POPOVER_POSITION_REFERENCE_WIDTH,
} from './ColorPickerField.constants'
import {
  useColorPickerFieldDraft,
  useColorPickerFieldHandlers,
  useColorPickerFieldPopover,
} from './ColorPickerField.hooks'
import {
  getFieldPositionReference,
  getNormalizedFieldValue,
} from './ColorPickerField.utils'

import * as styles from './ColorPickerField.module.css'

function ColorPickerField({
  borderless,
  color,
  disabled,
  error,
  placeholder,
  valueLabelTextKey,
  valueTooltipTextKey,
  onBlur,
  onChange,
  onClear,
}) {
  const normalizedValue = getNormalizedFieldValue({
    color,
    infoTextKey: valueTooltipTextKey,
    textKey: valueLabelTextKey,
  })

  const committedColor = normalizedValue.color

  const [format, setFormat] = useState(COLOR_FORMAT_HEX)

  const inputWrapperRef = useRef(null)

  const { isMobileScreenSize } = useScreen()

  const {
    context,
    floatingStyles,
    getFloatingProps,
    handleClosePopover,
    handleOpenPopover,
    isOpen,
    refs,
  } = useColorPickerFieldPopover({ disabled })

  const { setFloating, setPositionReference, setReference } = refs

  const {
    draftFormat,
    draftPopoverColor,
    draftValueState,
    setDraftColor,
    setDraftFormat,
    setDraftPopoverColor,
    setDraftValueState,
  } = useColorPickerFieldDraft({ committedColor, format, isOpen })

  const {
    handleCancelPopover,
    handleInputBlur,
    handleInputChange,
    handleInputClear,
    handlePopoverChange,
    handleSavePopover,
  } = useColorPickerFieldHandlers({
    committedColor,
    disabled,
    draftPopoverColor,
    draftValueState,
    format,
    handleClosePopover,
    refs,
    setDraftColor,
    setDraftFormat,
    setDraftPopoverColor,
    setDraftValueState,
    setFormat,
    onBlur,
    onChange,
    onClear,
  })

  function handleSetInputWrapperRef(node) {
    inputWrapperRef.current = node
    setReference(node)
    setPositionReference(
      node
        ? getFieldPositionReference(
            node,
            FIELD_POPOVER_POSITION_REFERENCE_WIDTH
          )
        : null
    )
  }

  const popover = (
    <ColorPickerPopover
      activeKey={FIELD_ACTIVE_VALUE_KEY}
      colorFormat={draftFormat}
      isOpen={isOpen}
      value={{
        [FIELD_ACTIVE_VALUE_KEY]: {
          ...normalizedValue,
          color: draftPopoverColor,
        },
      }}
      onCancel={handleCancelPopover}
      onChange={handlePopoverChange}
      onChangeColorFormat={setDraftFormat}
      onSave={handleSavePopover}
    />
  )

  return (
    <>
      <div
        className={classNames(styles.inputWrapper, {
          [styles.disabled]: disabled,
        })}
        ref={handleSetInputWrapperRef}
        onClick={disabled ? undefined : handleOpenPopover}
      >
        <InputField
          borderless={borderless}
          className={styles.input}
          disabled={disabled}
          errorText={error}
          placeholder={placeholder}
          value={draftValueState.value}
          onBlur={handleInputBlur}
          onChange={handleInputChange}
          onClear={handleInputClear}
          onFocus={disabled ? undefined : handleOpenPopover}
        />
      </div>

      {isMobileScreenSize ? (
        isOpen ? (
          <Modal onClose={handleClosePopover}>
            <Box
              {...getFloatingProps()}
              className={styles.mobileBox}
              ref={setFloating}
            >
              {popover}
            </Box>
          </Modal>
        ) : null
      ) : (
        <FloatingSelectDropdown
          className={styles.dropdown}
          context={context}
          floatingStyles={floatingStyles}
          getFloatingProps={getFloatingProps}
          isOpen={isOpen}
          refs={refs}
        >
          {popover}
        </FloatingSelectDropdown>
      )}
    </>
  )
}

export default ColorPickerField
